import React from 'react'
import {
  ApolloClient,
  ApolloProvider,
  InMemoryCache,
  HttpLink,
  NormalizedCacheObject,
  ApolloLink,
  Observable,
} from '@apollo/client'
import { GetServerSidePropsContext } from 'next'
import fetch from 'isomorphic-unfetch'
import { parseCookies } from 'nookies'

import bugsnagClient from 'clients/bugsnag'
import { SESSION_TOKEN } from 'constants/cookies'
import { getLocalUtcOffset } from 'utilities/dates'
import { ENVIRONMENT_ENDPOINTS } from 'constants/api'
import possibleTypes from '../fragment-types.json'

// Simple type for props that include Apollo state
export type WithApolloSSRProps = {
  apolloState: NormalizedCacheObject
}

let apolloClient: ApolloClient<NormalizedCacheObject> | null = null

const ErrorBoundary =
  bugsnagClient.getPlugin('react')?.createErrorBoundary(React) ||
  React.Component

/**
 * Creates an Apollo client for server-side rendering
 */
export const createApolloClient = (
  ctx?: GetServerSidePropsContext
): ApolloClient<NormalizedCacheObject> => {
  // Use a mutable reference to the context so it can be updated after client creation
  // This allows middleware (like withCurrentUser) to set overrideCookies and have
  // subsequent operations pick up the new token
  const contextRef = { current: ctx }

  const request = async (operation: any) => {
    let token: string | null = null

    // First try to get from context/cookies (server-side)
    // Read from the mutable reference so we always get the latest context
    const currentCtx = contextRef.current
    if (currentCtx) {
      token =
        ((currentCtx as any).overrideCookies &&
          (currentCtx as any).overrideCookies[SESSION_TOKEN]) ||
        parseCookies(currentCtx)[SESSION_TOKEN]
    }

    // If no token from context, try to get from localStorage (client-side)
    if (!token && typeof window !== 'undefined') {
      token = localStorage.getItem(SESSION_TOKEN)
    }

    // If still no token, try to get from cookies directly (client-side fallback)
    if (!token && typeof window !== 'undefined') {
      const cookies = document.cookie.split(';').reduce((acc, cookie) => {
        const [key, value] = cookie.trim().split('=')
        acc[key] = value
        return acc
      }, {})
      token = cookies[SESSION_TOKEN] || null
    }

    // Merge headers instead of replacing them
    const existingHeaders = operation.getContext().headers || {}
    operation.setContext({
      headers: {
        ...existingHeaders,
        'Cache-Control': 'no-cache',
        authorization: token ? `Bearer ${token}` : '',
        local_utc_offset: getLocalUtcOffset(),
      },
    })

    return operation
  }

  const httpLink = new HttpLink({
    uri: ENVIRONMENT_ENDPOINTS[process.env.APP_ENV || 'development'],
    fetch,
  })

  const requestLink = new ApolloLink(
    (operation, forward) =>
      new Observable(observer => {
        let handle
        Promise.resolve(operation)
          .then(request)
          .then(oper => {
            handle = forward(oper).subscribe({
              next: observer.next.bind(observer),
              error: observer.error.bind(observer),
              complete: observer.complete.bind(observer),
            })
          })
          .catch(observer.error.bind(observer))

        return () => {
          if (handle) handle.unsubscribe()
        }
      })
  )

  const client = new ApolloClient({
    link: requestLink.concat(httpLink),
    cache: new InMemoryCache({
      possibleTypes,
      typePolicies: {
        Query: {
          fields: {
            workout: {
              keyArgs: ['id'],
              read(_, { args, toReference }) {
                return args?.id
                  ? toReference({ __typename: 'Workout', id: args.id })
                  : undefined
              },
            },
            client: {
              keyArgs: ['id'],
              read(_, { args, toReference }) {
                return args?.id
                  ? toReference({ __typename: 'Client', id: args.id })
                  : undefined
              },
            },
          },
        },
        BridgePayer: {
          keyFields: ['name'],
        },
      },
    }),
    ssrMode: true, // Enable SSR mode
    defaultOptions: {
      watchQuery: {
        errorPolicy: 'all',
      },
      query: {
        errorPolicy: 'all',
      },
    },
  })

  // Attach the context reference to the client so it can be updated
  // This allows middleware to update overrideCookies and have subsequent operations pick it up
  ;(client as any).__contextRef = contextRef

  return client
}

/**
 * Creates an Apollo client for client-side rendering
 */
const createClientApolloClient = (
  initialState?: NormalizedCacheObject
): ApolloClient<NormalizedCacheObject> => {
  // Make sure to create a new client for every server-side request so that data
  // isn't shared between connections (which would be bad)
  if (typeof window === 'undefined') {
    return createApolloClient()
  }

  // Reuse client on the client-side (same pattern as withApollo)
  if (!apolloClient) {
    apolloClient = createApolloClient()
  }

  return apolloClient
}

/**
 * Initialize Apollo client with state
 */
const initApolloClient = (initialState?: NormalizedCacheObject) => {
  const client = createClientApolloClient(initialState)

  // If your page has Next.js data fetching methods that use Apollo Client, the initial state
  // gets hydrated here (same pattern as withApollo)
  if (initialState) {
    // Get existing cache, loaded during client side data fetching
    const existingCache = client.extract()
    // Merge the existing cache into data passed from getStaticProps/getServerSideProps
    const data = {
      ...existingCache,
      ...initialState,
    }
    // Restore the cache with the merged data
    client.cache.restore(data)
  }

  return client
}

/**
 * HOC wrapper for using Apollo in getServerSideProps
 *
 * @param getServerSideProps - Optional original getServerSideProps function
 * @returns Enhanced getServerSideProps function with Apollo client
 *
 * @example
 * ```typescript
 * export const getServerSideProps: GetServerSideProps = withApolloSSR()(async (context) => {
 *   const { apolloClient } = context
 *
 *   // Use Apollo client to fetch data
 *   const { data } = await apolloClient.query({
 *     query: GET_USER_DATA,
 *     variables: { id: context.params?.id }
 *   })
 *
 *   return {
 *     props: {
 *       userData: data.user,
 *       // apolloState will be automatically added
 *     }
 *   }
 * })
 * ```
 */
export const withApolloSSR = <T = {},>() => {
  return (getServerSideProps?: any) => {
    return async (context: GetServerSidePropsContext) => {
      // Create Apollo client for this request
      // Pass the context so the client can maintain a reference to it
      const apolloClient = createApolloClient(context)

      // Store a reference to update the context in the Apollo client
      // This allows middleware to update overrideCookies and have it picked up
      if ((apolloClient as any).__contextRef) {
        ;(apolloClient as any).__contextRef.current = context
      }

      // Add Apollo client to context for use in the getServerSideProps function
      const enhancedContext = {
        ...context,
        apolloClient,
      }

      try {
        // Call the original getServerSideProps if provided
        if (getServerSideProps) {
          const result = await getServerSideProps(enhancedContext)

          // If there's a redirect or notFound, return early
          if ('redirect' in result || 'notFound' in result) {
            return result
          }

          // Extract Apollo state from cache
          const apolloState = apolloClient.cache.extract()

          return {
            ...result,
            props: {
              ...result.props,
              apolloState,
            },
          }
        }

        // If no getServerSideProps provided, just return Apollo state
        const apolloState = apolloClient.cache.extract()

        return {
          props: {
            apolloState,
          } as T & WithApolloSSRProps,
        }
      } catch (error) {
        console.error('Error in withApolloSSR:', error)

        // Return error props or re-throw based on your error handling strategy
        return {
          props: {
            error: error instanceof Error ? error.message : 'An error occurred',
            apolloState: apolloClient.cache.extract(),
          } as unknown as T & WithApolloSSRProps,
        }
      }
    }
  }
}

// Export a simpler version for basic usage
export const withApolloSSRBasic = <T = {},>() => {
  return (getServerSideProps?: any) => {
    return async (context: GetServerSidePropsContext) => {
      const apolloClient = createApolloClient(context)

      if (getServerSideProps) {
        const result = await getServerSideProps({
          ...context,
          apolloClient,
        } as any)

        if ('redirect' in result || 'notFound' in result) {
          return result
        }

        return {
          ...result,
          props: {
            ...result.props,
            apolloState: apolloClient.cache.extract(),
          },
        }
      }

      return {
        props: {
          apolloState: apolloClient.cache.extract(),
        } as T & WithApolloSSRProps,
      }
    }
  }
}

/**
 * HOC wrapper that provides Apollo context for components using withApolloSSR
 * This is the equivalent of withApollo but for getServerSideProps pages
 */
export function withApolloSSRProvider<
  P extends WithApolloSSRProps = WithApolloSSRProps
>(PageComponent: React.ComponentType<P>, { ssr = true } = {}) {
  const WithApolloSSRProvider = (props: P) => {
    const { apolloState, ...pageProps } = props

    // Use the same Apollo client instance pattern as withApollo
    // This ensures the client-side Apollo client is the same instance
    // that would have been used if we had run getDataFromTree
    const client = initApolloClient(apolloState)

    return (
      <ErrorBoundary>
        <ApolloProvider client={client}>
          <PageComponent {...(pageProps as P)} />
        </ApolloProvider>
      </ErrorBoundary>
    )
  }

  // Set the correct displayName in development
  if (process.env.NODE_ENV !== 'production') {
    const displayName =
      PageComponent.displayName || PageComponent.name || 'Component'

    if (displayName === 'App') {
      console.warn(
        'This withApolloSSRProvider HOC only works with PageComponents.'
      )
    }

    WithApolloSSRProvider.displayName = `withApolloSSRProvider(${displayName})`
  }

  return WithApolloSSRProvider
}
